// @ts-strict-ignore
import { LabelingArticleConversionDataManager } from '../../../manager/labeling-article-conversion-data.manager';
import { BaseMultipleConnectionConnectProcessor } from './base-multiple-connection-connect.processor';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ConnectArticlePrepareData } from '../insert/connect-article-prepare-data';
import { UserUtils } from '@user/service/user-utils';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { LabelingArticleRotationHelper } from '../../../../label/helper/labeling-article-rotation.helper';
import { ArticleUtils } from '@article/util/article-utils';
import { ConnectionAreaVO, TextureAreaVO } from '@shared/vo/meta-element.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { LabelEditorStateManager } from '../../../../label/label-editor/service/label-editor-state.manager';
import { LabelEditorMode } from '../../../../label/label-editor/label-editor.component';
import { Injectable } from '@angular/core';
import { ArticleContainerFactory } from '@3d-scene/model3d/article-container.factory';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleManager } from '@article/manager/article.manager';
import { CompositeArticleCacheManager } from '@article/manager/composite-article-cache.manager';
import { ArticleContainerLoader } from '@article/service/article-container-loader';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { ProjectModel } from '@project/model/project.model';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { OpenLabelEditorService } from '../../../../label/label-editor/service/open-label-editor.service';
import { ProjectModelService } from '@project/service/project-model.service';
import { ProjectArticleService } from '@project/service/project-article.service';
import { SelectionModelService } from '../../../service/selection-model.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { RotateArticlePresenter } from '../../../presenter/rotate-article.presenter';
import { ProjectHelper } from '@project/helper/project.helper';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';

@Injectable({ providedIn: 'root' })
export class LabelingArticleConnectProcessor extends BaseMultipleConnectionConnectProcessor {
  constructor(
    projectModel: ProjectModel,
    projectModelService: ProjectModelService,
    projectArticleService: ProjectArticleService,
    projectMetaElementService: ProjectMetaElementService,
    localeResourceManager: LocaleResourceManager,
    articleContainerLoader: ArticleContainerLoader,
    compositeArticleCacheManager: CompositeArticleCacheManager,
    articleContainerFactory: ArticleContainerFactory,
    articleManager: ArticleManager,
    compositeArticleUtils: CompositeArticleUtils,
    compositeArticleInfoUtils: CompositeArticleInfoUtils,
    connectionInfoService: ConnectionInfoService,
    containerService: ContainerService,
    articleContainerUtils: ArticleContainerUtils,
    detachArticleUsageService: DetachArticleUsageService,
    private projectHelper: ProjectHelper,
    protected labelingArticleRotationHelper: LabelingArticleRotationHelper,
    private dataManager: LabelingArticleConversionDataManager,
    private selectionModelService: SelectionModelService,
    private labelEditorStateManager: LabelEditorStateManager,
    private openLabelEditorService: OpenLabelEditorService,
    protected rotateArticlePresenter: RotateArticlePresenter,
  ) {
    super(
      projectModel,
      projectModelService,
      projectArticleService,
      projectMetaElementService,
      localeResourceManager,
      articleContainerLoader,
      compositeArticleCacheManager,
      articleContainerFactory,
      articleManager,
      compositeArticleUtils,
      compositeArticleInfoUtils,
      connectionInfoService,
      containerService,
      articleContainerUtils,
      detachArticleUsageService,
    );
  }

  protected get reversedConnectionAvailable(): boolean {
    return false;
  }

  protected internalPreProcess(article: ArticleVO, connectPrepareDatas: ConnectArticlePrepareData[], handler: Function): void {
    const hasPermissionToEditAllLabelings: boolean = UserUtils.hasPermissionToEditAllLabelings(this.projectModel.user);
    const acceptingCubes: Connection[] = this.collectAcceptingCubesByPrepareDatas(connectPrepareDatas);
    if (!(hasPermissionToEditAllLabelings || ConnectionInfoUtils.isDefaulLabelingConnectionCubes(acceptingCubes))) {
      throw new Error(this.localeResourceManager.getValueByKey('connectProcessor.message.error_connect_labeling'));
    }
    super.internalPreProcess(article, connectPrepareDatas, handler);
  }

  public process(
    containers: ArticleContainer[],
    baseContainer: ArticleContainer,
    givingConnectionAreas: ConnectionAreaVO[],
    rotationAngle: number
  ): void {
    if (this.projectHelper.isLabelingCorrectionDisabled()) {
      ArticleUsageUtils.setLabelingCorrectionIgnored(baseContainer.articleUsage, true);
    }
    super.process(containers, baseContainer, givingConnectionAreas, rotationAngle);
  }

  protected createMetaElementUsages(
    containers: ArticleContainer[],
    givingConnectionCubes: Connection[],
    acceptingConnectionCubes: Connection[]
  ): void {
    super.createMetaElementUsages(containers, givingConnectionCubes, acceptingConnectionCubes);
    this.labelingArticleRotationHelper.rotateLabelingArticle(this.rotateArticlePresenter, containers[0], false, false);
  }

  public postProcess(containers: ArticleContainer[]): void {
    if (!this.dataManager.isConverting) {
      if (this.dataManager.stopPostProcess) {
        return;
      }
      this.selectionModelService.selectArticlesByUsageIds(containers.map((c) => c.articleUsage.id));
      if (this.labelEditorStateManager.labelEditorMode === LabelEditorMode.CLOSED) {
        const textureConnections: Connection[] = [];
        for (const articleContainer of containers) {
          const textureMetaElements: TextureAreaVO[] = ArticleUtils.getTextureElements(articleContainer.article);
          for (const textureArea of textureMetaElements) {
            const cube: Connection = articleContainer.findFirstConnectionByName(textureArea.name);
            if (cube) {
              textureConnections.push(cube);
            }
          }
        }
        this.openLabelEditorService.openForData({
          connectionCubes: textureConnections,
          validateSizes: true,
          selectAllCellsOnInit: false,
        });
      }
    } else {
      this.dataManager.applyDataToContainers(containers);
    }
  }
}
